import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, FileText, Lock, Send, Wallet, CreditCard, type LucideIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { StatCard } from "@/components/PagePrimitives";
import { Eyebrow } from "@/components/PageUi";
import { useModules } from "@/auth/ModulesContext";
import { moduleRequestAccessMessage, type ModuleKey } from "@/lib/modules";
import { cn } from "@/lib/utils";

interface Module {
  code: "AR" | "AP" | "EXP" | "PAY";
  title: string;
  subtitle: string;
  icon: LucideIcon;
  to?: string;
  pending?: string;
  soon?: boolean;
  /** Entitlement this card needs. Cards without one are not sellable yet ("soon"). */
  module?: ModuleKey;
}

const MODULES: Module[] = [
  {
    code: "AR",
    title: "Accounts receivable",
    subtitle: "Invoice, chase, reconcile",
    icon: FileText,
    to: "/ar",
    pending: "Send invoices",
    module: "ar",
  },
  {
    code: "AP",
    title: "Accounts payable",
    subtitle: "Approve bills, schedule payments",
    icon: Send,
    to: "/ap",
    pending: "Approve bills",
    module: "ap",
  },
  {
    code: "EXP",
    title: "Expenses",
    subtitle: "Card spend and receipts",
    icon: Wallet,
    to: "/exp",
    pending: "Review spend",
    module: "expenses",
  },
  {
    code: "PAY",
    title: "Payroll",
    subtitle: "Run salaries and filings",
    icon: CreditCard,
    soon: true,
  },
];

export function HomePage() {
  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col p-4 sm:p-6">
      <div className="mb-10 pt-4 sm:pt-8">
        <Eyebrow className="mb-5 font-mono">Your financial operations, connected</Eyebrow>
        <h1 className="display-headline display-headline--hero display-gradient display-gradient--twoline">
          Your platform.
          <br />
          Powered by Dolfin.
        </h1>
        <p className="mt-4 max-w-2xl text-base text-muted-foreground">
          Explore the financial ops agents your SMBs would use every day. Open the agent at any
          time with <kbd className="rounded border bg-muted px-1.5 py-0.5 text-xs">⌘I</kbd>, or
          jump to any page with <kbd className="rounded border bg-muted px-1.5 py-0.5 text-xs">⌘K</kbd>.
        </p>
      </div>

      {/* Illustrative figures, tagged Demo so nobody mistakes them for their own books. */}
      <div className="mb-8 grid grid-cols-2 gap-4 @3xl:grid-cols-4">
        {STATS.map((s) => (
          <StatCard
            key={s.label}
            label={s.label}
            value={s.value}
            sub={s.hint}
            trend={s.hintTone === "positive" ? "up" : undefined}
            placeholder
          />
        ))}
      </div>

      <Eyebrow className="mb-3">Modules</Eyebrow>
      <div className="grid grid-cols-1 gap-4 @xl:grid-cols-2">
        {MODULES.map((m) => (
          <ModuleCard key={m.code} module={m} />
        ))}
      </div>
    </div>
  );
}

interface Stat {
  label: string;
  value: string;
  hint: string;
  hintTone?: "default" | "positive";
}

const STATS: Stat[] = [
  { label: "Outstanding", value: "€59,110", hint: "3 invoices" },
  { label: "Received · 7d", value: "€3,900", hint: "+12% vs last", hintTone: "positive" },
  { label: "Bills to pay", value: "€12,400", hint: "4 awaiting" },
  { label: "Cash on hand", value: "€48,210", hint: "2 accounts" },
];

function ModuleCard({ module }: { module: Module }) {
  const navigate = useNavigate();
  const { hasModule } = useModules();
  const { icon: Icon, soon, title, subtitle, pending, to, module: moduleKey } = module;
  // Entitlements are unknown until /v1/me lands, and unknown reads as
  // entitled, so a slow lookup never flashes a lock over a module they own.
  const forbidden = !soon && !!moduleKey && !hasModule(moduleKey);
  const interactive = !soon && !forbidden && to;

  return (
    <div
      role={interactive ? "link" : undefined}
      tabIndex={interactive ? 0 : -1}
      onClick={interactive ? () => navigate(to!) : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(to!);
              }
            }
          : undefined
      }
      className={cn(
        "group relative overflow-hidden rounded-xl border bg-card/80 backdrop-blur-md transition duration-200",
        interactive &&
          "cursor-pointer motion-safe:hover:-translate-y-0.5 hover:shadow-lg hover:shadow-foreground/5 hover:border-foreground/20 hover:bg-card focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
        soon && "border-dashed",
      )}
      aria-disabled={forbidden || undefined}
    >
      {forbidden && (
        <div className="absolute inset-0 z-10 grid place-items-center bg-background/55 backdrop-blur-[1px]">
          <button
            type="button"
            onClick={() => toast.info(moduleRequestAccessMessage(moduleKey!))}
            className="flex items-center gap-1.5 rounded-full border border-border bg-background/95 px-3 py-1.5 text-xs font-semibold text-muted-foreground shadow-sm transition hover:border-foreground/30 hover:text-foreground"
          >
            <Lock className="size-3" />
            Forbidden - Request Access
          </button>
        </div>
      )}
      <div className={cn("relative flex items-start gap-4 p-5", forbidden && "opacity-45 grayscale", soon && "opacity-60")}>
        <span className="grid size-10 shrink-0 place-items-center rounded-xl border bg-muted/50 text-muted-foreground">
          <Icon className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h2 className="font-semibold">{title}</h2>
            {soon ? (
              <Badge variant="outline" className="text-[10px] tracking-wider uppercase">
                Soon
              </Badge>
            ) : null}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
          {!soon && pending && (
            <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-foreground">
              {pending}
              <ChevronRight className="size-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
